import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import { getWidthInPercent } from './helpers/bar-chart.helpers';

export type ElementBarProps = {
  /** Proposal (or current) value drawn on top, in {@link color}. */
  value: number;
  /**
   * Baseline (pre-proposal) value drawn behind in {@link baselineColor}.
   * When omitted or equal to {@link value}, no overlay is rendered.
   */
  baselineValue?: number;
  /** Shared scale for both bars. */
  maxValue: number;
  color: string;
  baselineColor?: string;
  /** Track background behind both bars. Default transparent. */
  trackColor?: string;
  height?: number | string;
  borderRadius?: number | string;
};

/**
 * Combined baseline + proposal horizontal bar. Renders the baseline value as a
 * muted background bar with the proposal value drawn on top in {@link color},
 * so the reduction (or increase) vs baseline is visible at a glance.
 *
 * Mirrors the in-row HBar pattern used by `ElementListRow` but without the
 * row-specific cost/segment overlays, so it can be reused anywhere a single
 * proposal-vs-baseline magnitude needs to be shown (e.g. report dashboard
 * SBEF groups).
 */
export const ElementBar: React.FC<ElementBarProps> = ({
  value,
  baselineValue,
  maxValue,
  color,
  baselineColor = BASELINE_COLOR,
  trackColor = 'transparent',
  height = 14,
  borderRadius = 2,
}) => {
  const { classes } = useStyles({ height, borderRadius, trackColor });
  const valuePct = getWidthInPercent(value, maxValue);
  // Mirror the in-row HBar pattern (`ElementListRow`): render the baseline
  // whenever the caller passes a number, even when it equals (or is below)
  // the proposal value. Width-0 collapses on its own and the colored
  // proposal bar then visually overrides identical magnitudes, while
  // reductions reveal the trailing gray sliver against the track.
  const showBaseline = baselineValue != null;
  const baselinePct = showBaseline
    ? getWidthInPercent(baselineValue, maxValue)
    : 0;

  return (
    <Box className={classes.track}>
      {showBaseline && (
        <Box
          className={classes.baseline}
          style={{
            width: `${baselinePct}%`,
            backgroundColor: baselineColor,
          }}
        />
      )}
      <Box
        className={classes.value}
        style={{ width: `${valuePct}%`, backgroundColor: color }}
      />
    </Box>
  );
};

const useStyles = makeStyles<{
  height: number | string;
  borderRadius: number | string;
  trackColor: string;
}>()((_theme, { height, borderRadius, trackColor }) => ({
  track: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height,
    borderRadius,
    backgroundColor: trackColor,
  },
  baseline: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
  },
  value: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
  },
}));
